import { computed } from 'vue';
import { useBuildStore } from '@apparatix/stores/useBuildStore';
import { useSessionStore } from '@apparatix/stores/useSessionStore';
import { LocalStorageKeys } from '@apparatix/constants/LocalStorageKeys';
import { Milliseconds } from '@apparatix/constants/Milliseconds';
import { useLocalStorage } from './useLocalStorage';

/** Matches App\Support\BuildVersion::DEV — no manifest to hash, so nothing to compare. */
const DEV_BUILD_ID = 'dev';

const SNOOZE_DURATION = Milliseconds.fromMinutes(30);

/**
 * Chunk-load failures that mean "this tab is running a build whose assets have
 * been deleted". Vite's own `vite:preloadError` covers most cases, but Inertia's
 * resolvePageComponent and Vue async components can surface a bare rejection.
 */
const CHUNK_FAILURE_PATTERN = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

let snoozeTimer: ReturnType<typeof setTimeout> | null = null;

export const useBuildVersionCheck = () => {
  const { getItem, removeItem, setItem } = useLocalStorage();

  const clearSnooze = () => {
    useBuildStore().dismissedUntil = 0;
    removeItem(LocalStorageKeys.build.promptDismissedUntil);
  };

  /**
   * Drive the snooze off a timer rather than comparing against Date.now() at
   * render time — the clock isn't reactive, so a render-time comparison would
   * leave the prompt hidden until some unrelated re-render happened to occur.
   */
  const scheduleUnsnooze = () => {
    if (snoozeTimer !== null) {
      clearTimeout(snoozeTimer);
      snoozeTimer = null;
    }

    const remaining = useBuildStore().dismissedUntil - Date.now();

    if (remaining <= 0) {
      clearSnooze();
      return;
    }

    snoozeTimer = setTimeout(() => {
      snoozeTimer = null;
      clearSnooze();
    }, remaining);
  };

  /**
   * Seed what this tab loaded with. The contract is tracked even in dev, since
   * it is an explicit statement that old clients are incompatible; only the
   * build-hash comparison is dev-disabled, because dev chunks aren't hashed.
   */
  const seed = (buildId?: string, contractVersion?: number) => {
    const build = useBuildStore();

    build.bootContract = contractVersion ?? 0;

    if (!buildId || buildId === DEV_BUILD_ID || import.meta.env.DEV) {
      build.enabled = false;
      return;
    }

    build.enabled = true;
    build.bootBuildId = buildId;
    build.latestBuildId = buildId;
    // Survives a reload, so someone who snoozed and then navigated isn't
    // immediately re-prompted.
    build.dismissedUntil = getItem<number>(LocalStorageKeys.build.promptDismissedUntil) ?? 0;
    scheduleUnsnooze();
  };

  /**
   * Fold a fresh server report into the store. Contract first: it outranks the
   * build hash, and a tab that is contract-stale must not be offered a
   * dismissible prompt.
   */
  const noteServerVersions = (buildId?: string, contractVersion?: number) => {
    const build = useBuildStore();

    if (typeof contractVersion === 'number'
      && build.bootContract > 0
      && contractVersion > build.bootContract) {
      build.requireRefresh('contract');
      return;
    }

    if (!build.enabled || !buildId) {
      return;
    }

    build.latestBuildId = buildId;

    if (buildId !== build.bootBuildId) {
      build.updateAvailable = true;
    }
  };

  /** The dismissible prompt: only for an optional update, and only if not snoozed. */
  const shouldShowPrompt = computed(() => {
    const build = useBuildStore();

    return build.staleness === 'optional' && build.dismissedUntil === 0;
  });

  const snooze = () => {
    const build = useBuildStore();

    build.dismissedUntil = Date.now() + SNOOZE_DURATION;
    setItem({ key: LocalStorageKeys.build.promptDismissedUntil, value: build.dismissedUntil });
    scheduleUnsnooze();
  };

  const reload = () => {
    useSessionStore().suppressLeaveWarning = true;
    window.location.reload();
  };

  const isChunkFailure = (reason: unknown): boolean => {
    const message = reason instanceof Error
      ? reason.message
      : String((reason as { message?: string })?.message ?? reason ?? '');

    return CHUNK_FAILURE_PATTERN.test(message);
  };

  const onPreloadError = (event: Event) => {
    const build = useBuildStore();

    // In dev the chunks aren't content-hashed and HMR churn can trip this, so
    // leave Vite's default rethrow alone rather than blocking the page.
    if (!build.enabled) {
      return;
    }

    // Suppress Vite's default rethrow; we handle this by prompting a reload.
    event.preventDefault();
    build.requireRefresh('chunk-404');
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const build = useBuildStore();

    if (!build.enabled) {
      return;
    }

    if (isChunkFailure(event.reason)) {
      build.requireRefresh('chunk-404');
    }
  };

  /**
   * Register the hard-failure listeners. Called from AppFactory before mount so
   * a chunk that fails during initial page resolution is still caught.
   */
  const registerChunkFailureListeners = () => {
    window.addEventListener('vite:preloadError', onPreloadError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
  };

  return {
    isChunkFailure,
    noteServerVersions,
    registerChunkFailureListeners,
    reload,
    seed,
    shouldShowPrompt,
    snooze,
  };
};
